/**
 * Receiving, bins and anomalies — the client side of the spec of 2026-09-23.
 *
 * Two things live here besides the types:
 *
 *  • THE TABLET'S EMPLOYEES. Two people share one tablet; every action is logged under whoever
 *    did it. The switch re-authenticates (a real login), and each person who signed in on this
 *    tablet keeps their own session here, so a scan queued offline by Karim is replayed as Karim
 *    even if Sofia has taken the tablet since.
 *
 *  • THE OFFLINE QUEUE. When the network drops, a scan is kept with its command id and the
 *    employee who made it, shown as "N scans pending", and replayed ONCE when the network is
 *    back. The server answers a replayed command id with the first result and changes nothing,
 *    so a double replay is harmless (spec §10).
 */
import axios from 'axios';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import i18n from '../i18n';

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3000/api/v1';

// ── types ─────────────────────────────────────────────────────────────────────────────────────

export interface WaitingLine {
  lineId: string;
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  customerType: 'B2B' | 'B2C';
  qty: number;
  promisedAt: string | null;
  originalPromisedAt: string | null;
  orderedAt: string;
  orderedBy: string;
  channel: string;
  channelLabel: string;
  supplierName: string | null;
  late: number;
  due: boolean;
  rank: number;
  reportsCount: number;
  poStatus: string | null;
  poNumber: string | null;
  orderedAtNotYetSent: boolean;
}
export interface ReplenLine {
  supplierOrderLineId: string;
  poNumber: string;
  poStatus: string;
  supplierName: string;
  qty: number;
  promisedAt: string;
  late: number;
  due: boolean;
  location: string | null;
}
export interface ExpectedGroup {
  productId: string;
  name: string;
  brand: string | null;
  ref: string | null;
  ean: string | null;
  /** Every code a scan will match for this part (catalogue + the supplier part's EANs). */
  eans: string[];
  /** READY: a code to scan · REF_ONLY: scan the reference · NO_PO: not ordered yet · NOT_READY: fix on EAN prep. */
  scanStatus: 'READY' | 'REF_ONLY' | 'NO_PO' | 'NOT_READY';
  location: string | null;
  total: number;
  customerUnits: number;
  replenUnits: number;
  orders: number;
  split: string;
  suppliers: string[];
  orderedBy: string[];
  due: boolean;
  maxLate: number;
  customers: WaitingLine[];
  replenishment: ReplenLine[];
}
export interface ExpectedList {
  due: ExpectedGroup[];
  later: ExpectedGroup[];
  totalUnits: number;
}

export interface CardAllocation {
  slot: string;
  qty: number;
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  customerType: 'B2B' | 'B2C';
  lineQty: number;
  lineReceived: number;
  promisedAt: string | null;
  orderedAt: string;
  orderedBy: string;
  channel: string;
  channelLabel: string;
  orderUnitsIn: number;
  orderUnitsTotal: number;
  bin: {
    status: string;
    unitsIn: number;
    unitsDue: number;
    ordersDue: number;
    complete: boolean;
  } | null;
  undone: boolean;
}
export interface ScanCard {
  commandId: string;
  kind: 'CUSTOMER' | 'REPLENISHMENT' | 'SURPLUS' | 'UNKNOWN' | 'DAMAGED';
  ts: string;
  by: string;
  code: string;
  product: {
    id: string;
    name: string;
    brand: string | null;
    ref: string | null;
    ean: string | null;
  } | null;
  totalQty: number;
  allocations: CardAllocation[];
  replenishment: { qty: number; location: string | null } | null;
  quarantine: { qty: number; reason: string; anomalies: string[] } | null;
  alreadyToday: Array<{
    ts: string;
    qty: number;
    destination: string;
    slot: string | null;
    location: string | null;
    orderNumber: string | null;
    customerName: string | null;
    by: string;
  }>;
  stillExpected: number;
  undone: boolean;
  replayed?: boolean;
}
/** Spec §5: the same part again within 2 s with nothing expecting it — refused until confirmed. */
export interface DoubleScan {
  kind: 'DOUBLE_SCAN';
  code: string;
  productId: string | null;
}

/** One van (migration 133) with its running figures. */
export interface VanSummary {
  id: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  startedAt: string;
  startedBy: string;
  endedAt: string | null;
  endReason: 'OPERATOR' | 'DAY_CLOSE' | null;
  units: number;
  toCustomers: number;
  toShelf: number;
  toQuarantine: number;
  expectedToday: number;
  stillExpected: number;
}
export interface VanBoard {
  suppliers: Array<{
    supplierId: string;
    code: string;
    name: string;
    expectedToday: number;
    received: number;
    stillExpected: number;
    vansToday: number;
  }>;
  current: VanSummary | null;
  dayClosed: boolean;
}
/** The floor's Journal for one day (GET /receiving/journal). */
export interface Journal {
  day: string;
  documents: Array<{
    id: string;
    docType: string;
    number: string;
    date: string;
    customerName: string | null;
    orderId: string | null;
    createdBy: string | null;
  }>;
  actions: Array<{
    at: string;
    actor: string;
    role: string | null;
    action: string;
    entity: string;
    detail: unknown;
  }>;
}
export type ScanResponse =
  | (ScanCard & { van?: VanSummary | null })
  | AmbiguousScan
  | DoubleScan
  | PickLineScan
  | PackConfirmScan;

/** Migration 138 — nothing recognised the code: which of this van's expected parts is it? */
export interface PickLineScan {
  kind: 'PICK_LINE';
  code: string;
  /** Feedback 3.12 — the code named a part, but NO supplier was asked for it (never ordered). */
  unexpectedProduct?: { productId: string; sku: string; name: string; brand: string | null } | null;
  candidates: Array<{
    productId: string;
    sku: string;
    name: string;
    brand: string | null;
    reference: string | null;
    outstanding: number;
  }>;
}
/** Migration 138 — the code stands for a pack: confirm "1 scan = N units" once. */
export interface PackConfirmScan {
  kind: 'PACK_CONFIRM';
  code: string;
  productId: string;
  packQty: number;
  productName: string | null;
}

export interface AmbiguousScan {
  kind: 'AMBIGUOUS';
  code: string;
  candidates: Array<{ productId: string; sku: string; name: string; brand: string | null }>;
}

export interface ScanLogRow {
  id: string;
  commandId: string;
  ts: string;
  qty: number;
  destination: 'CUSTOMER' | 'REPLENISHMENT' | 'QUARANTINE';
  reason: string | null;
  slot: string | null;
  location: string | null;
  code: string;
  productName: string | null;
  brand: string | null;
  ref: string | null;
  orderNumber: string | null;
  customerName: string | null;
  by: string;
  role: string;
  undoneAt: string | null;
  undoneBy: string | null;
  undoReason: string | null;
  anomaly: string | null;
}
export interface ScanLog {
  day: string;
  stats: { units: number; toCustomers: number; toStock: number; toQuarantine: number };
  data: ScanLogRow[];
}

export interface BinLine {
  lineId: string;
  orderId: string;
  orderNumber: string;
  productId: string;
  productName: string;
  brand: string | null;
  ref: string | null;
  ean: string | null;
  source: 'INTERNAL' | 'EXTERNAL';
  status: string;
  qty: number;
  received: number;
  picked: number;
  delivered: number;
  promisedAt: string | null;
  orderedBy: string;
  channel: string;
  inBin: boolean;
  shelfLocation: string | null;
  hasPickupCode: boolean;
  pickupLockedAt: string | null;
}
export interface Bin {
  id: string;
  slot: string;
  status: 'OPEN' | 'COMPLETE' | 'PREPARED';
  customerId: string | null;
  customerName: string | null;
  customerType: 'B2B' | 'B2C';
  openedAt: string;
  openedBy: string | null;
  completedAt: string | null;
  preparedAt: string | null;
  orders: Array<{ orderId: string; orderNumber: string; orderedBy: string; inBin: boolean }>;
  unitsIn: number;
  unitsDue: number;
  ordersDue: number;
  inBin: BinLine[];
  waitingToday: BinLine[];
  expectedLater: BinLine[];
  inStockToPick: BinLine[];
  lines: BinLine[];
}
export interface BinsBoard {
  slots: string[];
  bins: Bin[];
}

export interface CallEntry {
  id: string;
  day: string;
  lineId: string;
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  customerPhone: string | null;
  customerType: 'B2B' | 'B2C';
  orderedBy: string;
  channel: string;
  channelLabel: string;
  productName: string;
  ref: string | null;
  brand: string | null;
  missingQty: number;
  supplierName: string | null;
  oldDate: string | null;
  newDate: string | null;
  reportNo: number;
  closedBy: string | null;
  closedAt: string;
  decision: 'WAIT' | 'SWITCH' | 'CANCEL' | 'ARRIVED_LATE' | 'ORDER_CANCELLED' | null;
  decidedBy: string | null;
  decidedAt: string | null;
  consentBy: string | null;
  decisionDetail: DecisionOutcome | null;
  anomaly: string | null;
  escalated: boolean | null;
  missingTTC: string;
  creditLimit: string | null;
  exposure: string;
  paymentMethod: string | null;
  depositInvoices: string | null;
  paid: string;
  orderTotal: string;
}
export interface DecisionOutcome {
  decision: string;
  docs: Array<{ type: string; id: string | null; number: string; text: string }>;
  effects: string[];
  exposure?: { before: number; after: number; limit: number | null } | null;
  message: string;
  refund?: { amount: number; method: string } | null;
}
export interface Alternative {
  supplierId: string;
  supplierName: string;
  costHT: number;
  unitHT: number;
  vatRate: number;
  unitTTC: number;
  lineTTC: number;
  leadTimeDays: number | null;
  availableQty: number | null;
}

export type AnomalyStatus = 'OPEN' | 'FOLLOW_UP' | 'RESOLVED' | 'CANCELLED';
export type AnomalyOwner = 'COUNTER' | 'PURCHASING' | 'WAREHOUSE' | 'MANAGER';
export interface Anomaly {
  id: string;
  number: string;
  type: string;
  typeLabel: string;
  status: AnomalyStatus;
  owner: AnomalyOwner;
  ownerLabel: string;
  customerType: 'B2B' | 'B2C' | null;
  customerId: string | null;
  customerName: string | null;
  orderId: string | null;
  orderNumber: string | null;
  orderLineId: string | null;
  productId: string | null;
  productName: string | null;
  brand: string | null;
  ref: string | null;
  code: string | null;
  qty: number | null;
  supplierName: string | null;
  nextAction: string | null;
  reportsCount: number;
  escalated: boolean;
  openedAt: string;
  openedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
  resolution: string | null;
  daysOpen: number;
  creditLimit: string | null;
  exposure: string | null;
  paymentMethod: string | null;
  depositInvoice: string | null;
  canClose: boolean;
}
export interface AnomalyRegister {
  filters: { owner: string; status: string; customerType: string };
  counters: {
    open: number;
    missing: number;
    escalated: number;
    garages: number;
    consumers: number;
  };
  data: Anomaly[];
}
export interface HistoryEvent {
  at: string;
  who: string | null;
  kind: string;
  what: string;
}

// ── formatting ────────────────────────────────────────────────────────────────────────────────

export const fmtTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
export const fmtDay = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : '—';
export const fmtOrderedOn = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })} ${fmtTime(iso)}`;
};
export const fmtEur = (n: number | string | null | undefined): string =>
  n == null || n === ''
    ? '—'
    : Number(n).toLocaleString(undefined, { style: 'currency', currency: 'EUR' });

// ── the tablet's employees ───────────────────────────────────────────────────────────────────

export interface TabletSession {
  userId: string;
  name: string;
  role: string;
  accessToken: string;
  refreshToken: string | null;
}
const SESSIONS_KEY = 'mecadepot.tablet.sessions';

function readSessions(): Record<string, TabletSession> {
  try {
    return JSON.parse(localStorage.getItem(SESSIONS_KEY) ?? '{}') as Record<string, TabletSession>;
  } catch {
    return {};
  }
}
function writeSessions(s: Record<string, TabletSession>): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable */
  }
}

/** Remember the signed-in employee on this tablet (called on every page load and on a switch). */
export function rememberCurrentEmployee(name: string, role: string): void {
  const user = useAuthStore.getState().user;
  const token = localStorage.getItem('auth_token');
  if (!user || !token) return;
  const all = readSessions();
  all[user.sub] = {
    userId: user.sub,
    name,
    role,
    accessToken: token,
    refreshToken: localStorage.getItem('refresh_token'),
  };
  writeSessions(all);
}

/**
 * Switch employee: a real login, so the person handing the tablet over is not trusted to say
 * who takes it. The new session becomes the app's session; the old one stays on the tablet only
 * so its queued scans can still be replayed in its name.
 */
export async function switchEmployee(email: string, password: string): Promise<void> {
  const { data } = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
    `${API_BASE_URL}/auth/login`,
    { email, password }
  );
  localStorage.setItem('refresh_token', data.data.refreshToken);
  const [, payload] = data.data.accessToken.split('.');
  const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
    sub: string;
    role: string;
    customerId?: string | null;
    email?: string;
    mustChangePassword?: boolean;
  };
  useAuthStore.getState().setSession(data.data.accessToken, {
    sub: claims.sub,
    role: claims.role as never,
    customerId: claims.customerId,
    email: claims.email,
    mustChangePassword: claims.mustChangePassword === true,
  });
}

// ── offline queue ────────────────────────────────────────────────────────────────────────────

export interface QueuedScan {
  commandId: string;
  body: { code: string; qty?: number; damaged?: boolean };
  userId: string;
  userName: string;
  queuedAt: string;
}
const QUEUE_KEY = 'mecadepot.receiving.queue';

export function readQueue(): QueuedScan[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as QueuedScan[];
  } catch {
    return [];
  }
}
function writeQueue(q: QueuedScan[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* storage unavailable */
  }
}
export function enqueueScan(item: QueuedScan): void {
  writeQueue([...readQueue(), item]);
}

/** True when the request never reached the server (network down, DNS, timeout). */
export function isNetworkError(err: unknown): boolean {
  const e = err as { response?: unknown; code?: string; message?: string };
  return (
    !e?.response &&
    (e?.code === 'ERR_NETWORK' ||
      e?.code === 'ECONNABORTED' ||
      /Network Error/i.test(e?.message ?? ''))
  );
}

async function postAs(session: TabletSession, item: QueuedScan): Promise<ScanResponse> {
  const send = (token: string) =>
    axios.post<ScanResponse>(`${API_BASE_URL}/receiving/scans`, item.body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Command-Id': item.commandId,
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });
  try {
    return (await send(session.accessToken)).data;
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status !== 401 || !session.refreshToken) throw err;
    // That employee's own session has expired: rotate it with THEIR refresh token, never the
    // current user's, so the scan stays in their name.
    const { data } = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken: session.refreshToken }
    );
    const all = readSessions();
    all[session.userId] = {
      ...session,
      accessToken: data.data.accessToken,
      refreshToken: data.data.refreshToken,
    };
    writeSessions(all);
    if (useAuthStore.getState().user?.sub === session.userId) {
      localStorage.setItem('auth_token', data.data.accessToken);
      localStorage.setItem('refresh_token', data.data.refreshToken);
    }
    return (await send(data.data.accessToken)).data;
  }
}

/**
 * Replay the queue, oldest first, each scan under the employee who made it. A scan that the
 * network still refuses stays queued; one the server refused for good (a 4xx) leaves the queue
 * and is reported, because retrying it would be refused again.
 */
export async function replayQueue(): Promise<{
  sent: ScanResponse[];
  failed: Array<{ item: QueuedScan; message: string }>;
}> {
  const sent: ScanResponse[] = [];
  const failed: Array<{ item: QueuedScan; message: string }> = [];
  const sessions = readSessions();
  let q = readQueue();
  for (const item of [...q]) {
    const session = sessions[item.userId];
    if (!session) {
      failed.push({ item, message: i18n.t('rcv.errors.signedOut', { name: item.userName }) });
      q = q.filter((x) => x.commandId !== item.commandId);
      continue;
    }
    try {
      sent.push(await postAs(session, item));
      q = q.filter((x) => x.commandId !== item.commandId);
    } catch (err) {
      if (isNetworkError(err)) break; // still offline: keep the rest, in order
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        i18n.t('rcv.errors.refused');
      failed.push({ item, message });
      q = q.filter((x) => x.commandId !== item.commandId);
    }
    writeQueue(q);
  }
  writeQueue(q);
  return { sent, failed };
}

// ── API ──────────────────────────────────────────────────────────────────────────────────────

export const receivingApi = {
  expected: () => apiClient.get<ExpectedList>('/receiving/expected').then((r) => r.data),
  scan: (
    body: {
      code: string;
      qty?: number;
      damaged?: boolean;
      productId?: string;
      confirmSecond?: boolean;
      learnLine?: boolean;
      unknownConfirmed?: boolean;
      confirmPack?: boolean;
    },
    commandId: string
  ) =>
    apiClient
      .post<ScanResponse>('/receiving/scans', body, { headers: { 'X-Command-Id': commandId } })
      .then((r) => r.data),
  vans: () => apiClient.get<VanBoard>('/receiving/vans').then((r) => r.data),
  journal: (day?: string) =>
    apiClient
      .get<Journal>('/receiving/journal', { params: day ? { day } : {} })
      .then((r) => r.data),
  startVan: (supplierId: string) =>
    apiClient.post<VanSummary>('/receiving/vans', { supplierId }).then((r) => r.data),
  endVan: (confirm?: boolean) =>
    apiClient.post<VanSummary>('/receiving/vans/end', { confirm }).then((r) => r.data),
  undoLast: (reason: string) =>
    apiClient
      .post<{
        commandId: string;
        undone: number;
        card: ScanCard;
      }>('/receiving/scans/undo-last', { reason })
      .then((r) => r.data),
  scanLog: (day?: string) =>
    apiClient
      .get<ScanLog>('/receiving/scan-log', { params: day ? { day } : {} })
      .then((r) => r.data),
  whereIs: (code: string) =>
    apiClient
      .get<{ data: Array<Record<string, unknown>> }>('/receiving/where-is', { params: { code } })
      .then((r) => r.data),
  closeDay: () =>
    apiClient
      .post<{
        brNumber: string;
        alreadyClosed: boolean;
        shortagesCreated: number;
        callList: CallEntry[];
      }>('/receiving/close-day', {})
      .then((r) => r.data),
  bins: () => apiClient.get<BinsBoard>('/bins').then((r) => r.data),
  prepare: (slot: string) =>
    apiClient.post(`/bins/${encodeURIComponent(slot)}/prepare`, {}).then((r) => r.data),
  handOver: (
    slot: string,
    body: {
      collectorName?: string;
      pickupCode?: string;
      idCheckedName?: string;
      scans: Array<{ code: string; qty?: number }>;
      notFound?: Array<{ lineId: string; reason: string }>;
      orderIds?: string[];
      lineIds?: string[];
    }
  ) =>
    apiClient
      .post<{
        deliveryNote: { id: string; number?: string; deliveryNoteNumber?: string } | null;
        stillExpected: number;
        orders?: Array<{
          id: string;
          receipt: { id: string; receiptNumber: string } | null;
          invoice: { id: string; invoiceNumber: string } | null;
        }>;
      }>(`/bins/${encodeURIComponent(slot)}/hand-over`, body)
      .then((r) => r.data),
  unlockPickup: (orderId: string, reason: string) =>
    apiClient.post(`/bins/pickup/${orderId}/unlock`, { reason }).then((r) => r.data),
  callList: (day?: string) =>
    apiClient
      .get<{
        day: string;
        data: CallEntry[];
        open: number;
      }>('/call-list', { params: day ? { day } : {} })
      .then((r) => r.data),
  alternatives: (lineId: string) =>
    apiClient.get<{ data: Alternative[] }>(`/call-list/${lineId}/alternatives`).then((r) => r.data),
  decide: (
    lineId: string,
    body: { decision: 'WAIT' | 'SWITCH' | 'CANCEL'; supplierId?: string; consent?: boolean }
  ) => apiClient.post<DecisionOutcome>(`/call-list/${lineId}/decision`, body).then((r) => r.data),
  cancelLine: (lineId: string, body: { qty?: number; reason: string; consent: boolean }) =>
    apiClient.post<DecisionOutcome>(`/order-lines/${lineId}/cancel`, body).then((r) => r.data),
  cancelOrder: (orderId: string, reason: string) =>
    apiClient
      .post<{
        putBack: Array<{ name: string; qty: number; location: string | null }>;
        putBackAnomaly: string | null;
        slot: string | null;
        exposure: { before: number; after: number } | null;
      }>(`/orders/${orderId}/cancel`, { reason })
      .then((r) => r.data),
  anomalies: (params: { status?: string; owner?: string; customerType?: string }) =>
    apiClient.get<AnomalyRegister>('/anomalies', { params }).then((r) => r.data),
  resolveAnomaly: (id: string, resolution: string, cancelledAtSupplier?: boolean) =>
    apiClient
      .post(`/anomalies/${id}/resolve`, { resolution, cancelledAtSupplier })
      .then((r) => r.data),
  history: (orderId: string) =>
    apiClient.get<{ data: HistoryEvent[] }>(`/orders/${orderId}/history`).then((r) => r.data),
};

/** The server's refusal message, or a generic one. */
export function apiMessage(err: unknown, fallback = i18n.t('rcv.errors.generic')): string {
  return (
    (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback
  );
}
